/**
 * Glass-styled menu for the learner experience.
 * Wraps React Aria Menu for keyboard navigation, focus management, and selection.
 */
import type { ReactNode, CSSProperties } from 'react';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger as AriaMenuTrigger,
  Separator as AriaSeparator,
  type MenuProps as AriaMenuProps,
  type MenuItemProps as AriaMenuItemProps,
  type MenuTriggerProps as AriaMenuTriggerProps,
} from 'react-aria-components';
import { Popover } from './Popover';

export type MenuTriggerProps = AriaMenuTriggerProps;
export function MenuTrigger(props: MenuTriggerProps) {
  return <AriaMenuTrigger {...props} />;
}

export interface MenuProps<T extends object> extends Omit<AriaMenuProps<T>, 'className' | 'style'> {
  placement?: 'bottom' | 'bottom start' | 'bottom end' | 'top' | 'top start' | 'top end';
}

export function Menu<T extends object>({
  placement = 'bottom end',
  children,
  ...props
}: MenuProps<T>) {
  return (
    <Popover placement={placement} offset={4}>
      <AriaMenu {...props} className="outline-none" style={{ padding: '4px 0', minWidth: 160 }}>
        {children}
      </AriaMenu>
    </Popover>
  );
}

export interface MenuItemProps extends Omit<AriaMenuItemProps, 'className' | 'style'> {
  variant?: 'default' | 'danger';
  icon?: ReactNode;
}

export function MenuItem({ variant = 'default', icon, children, ...props }: MenuItemProps) {
  const isDanger = variant === 'danger';
  return (
    <AriaMenuItem
      {...props}
      className="outline-none cursor-pointer"
      style={({ isFocused, isPressed }) => {
        const baseColor = isDanger ? 'var(--color-error)' : 'var(--color-text)';
        const style: CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 12px',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: 'var(--font-sans)',
          color: baseColor,
          background: isFocused || isPressed ? 'var(--color-surface-muted)' : 'transparent',
          transition: 'background 0.1s',
          borderRadius: 0,
        };
        return style;
      }}
    >
      {icon ? (
        <>
          <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>
          {children as ReactNode}
        </>
      ) : (
        children
      )}
    </AriaMenuItem>
  );
}

export function MenuSeparator() {
  return (
    <AriaSeparator
      style={{
        height: 1,
        background: 'var(--color-surface-border)',
        margin: '4px 0',
        border: 'none',
      }}
    />
  );
}
